/**
 * Códigos universais de produto.
 *
 * O código é o campo mais importante do cadastro: é por ele que a busca no
 * Mercado Livre acontece (RN-070) e é ele que decide a correspondência exata
 * ou incompatível (RN-073). Um dígito trocado silenciosamente compara o
 * produto errado — por isso a validação acontece no cadastro, não depois.
 */

export type TipoCodigo = 'GTIN' | 'EAN' | 'UPC' | 'ISBN' | 'OUTRO';

/** Comprimentos válidos por padrão. GTIN cobre 8, 12, 13 e 14 dígitos. */
const COMPRIMENTOS: Record<Exclude<TipoCodigo, 'OUTRO'>, number[]> = {
  GTIN: [8, 12, 13, 14],
  EAN: [8, 13],
  UPC: [12],
  ISBN: [13],
};

export const somenteDigitos = (valor: string): string => valor.replace(/\D/g, '');

/**
 * Dígito verificador do padrão GTIN (mod 10).
 *
 * Da direita para a esquerda, ignorando o próprio dígito verificador, os
 * algarismos são multiplicados alternadamente por 3 e 1.
 */
export function digitoVerificador(semDigito: string): number {
  let soma = 0;
  for (let i = 0; i < semDigito.length; i++) {
    const algarismo = Number(semDigito[semDigito.length - 1 - i]);
    soma += algarismo * (i % 2 === 0 ? 3 : 1);
  }
  return (10 - (soma % 10)) % 10;
}

export interface ResultadoValidacao {
  valido: boolean;
  motivo?: string;
  /** Valor limpo, pronto para gravar. Só presente quando válido. */
  normalizado?: string;
}

export function validarCodigo(tipo: TipoCodigo, valorBruto: string): ResultadoValidacao {
  const valor = somenteDigitos(valorBruto);

  if (tipo === 'OUTRO') {
    const livre = valorBruto.trim();
    return livre.length > 0
      ? { valido: true, normalizado: livre }
      : { valido: false, motivo: 'Código vazio' };
  }

  if (valor.length === 0) return { valido: false, motivo: 'Código vazio' };

  const esperados = COMPRIMENTOS[tipo];
  if (!esperados.includes(valor.length)) {
    const lista = esperados.join(', ');
    return {
      valido: false,
      motivo: `${tipo} tem ${lista} dígitos; este tem ${valor.length}`,
    };
  }

  const esperado = digitoVerificador(valor.slice(0, -1));
  const informado = Number(valor[valor.length - 1]);
  if (esperado !== informado) {
    return {
      valido: false,
      motivo: `Dígito verificador não confere: esperado ${esperado}, veio ${informado}. Confira se digitou certo.`,
    };
  }

  if (tipo === 'ISBN' && !valor.startsWith('978') && !valor.startsWith('979')) {
    return { valido: false, motivo: 'ISBN-13 começa com 978 ou 979' };
  }

  return { valido: true, normalizado: valor };
}

/** ID do documento em `codigosProduto`. É ele que garante a unicidade global (RN-020). */
export const idCodigo = (tipo: TipoCodigo, valor: string): string => `${tipo}_${valor}`;
